import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Group,
  Title,
  Paper,
  Loader,
  Center,
  TextInput,
  Text,
  Avatar,
  Card,
  Tabs,
  Modal,
  ScrollArea,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCash,
  IconSearch,
  IconUser,
  IconCalendar,
  IconId,
  IconClipboardList,
  IconStethoscope,
  IconUserSearch,
  IconTimeline,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  Encounter,
  Invoice,
  Patient,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useDebouncedValue } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { ResourceTable } from '@/components/resource-search/ResourceTable';
import { usePaymentSearch } from '../lims/hooks/usePaymentSearch';
import {
  useOrderSearch,
  ORDER_SEARCH_DEFAULTS,
} from '../lims/hooks/useOrderSearch';
import {
  useEncounterSearch,
  ENCOUNTER_SEARCH_DEFAULTS,
} from '../patient/encounters-tab/useEncounterSearch';
import { useEncounterDetails } from '../patient/encounters-tab/useEncounterDetails';
import {
  getPaymentTableColumns,
  PAYMENT_SEARCH_DEFAULTS,
  PaymentSearchParams,
} from '../lims/payments/paymentSearchConfig';
import { getOrderTableColumns } from '../lims/orders/orderSearchConfig';
import { getEncounterTableColumns } from '../patient/encounters-tab/encounterSearchConfig';
import { InvoiceDetailModal } from '../lims/payments/InvoiceDetailModal';
import { PatientTimeline } from './PatientTimeline';
import { EncounterDetailsCard } from '@/components/EncounterDetailsCard';
import { EditButton } from '@/components/EditButton';
import { PatientEditDemographicsModal } from '../patient/demographics-actions/PatientEditDemographicsModal';
import { validateAmka } from '@/utils/amka-validation';
import {
  formatDate,
  getPatientAmka,
  getPatientOrPractitionerName,
} from '../lims/utils/common-helpers';
import { IDENTIFIERS } from '@consts/systems.consts';

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

interface PatientRowProps {
  patient: Patient;
  onSelect: (patient: Patient) => void;
}

const PatientRow: FC<PatientRowProps> = ({ patient, onSelect }) => {
  const name = getPatientOrPractitionerName(patient);
  const amka = getPatientAmka(patient);

  return (
    <Card
      shadow="none"
      withBorder={false}
      radius="sm"
      p="sm"
      onClick={() => onSelect(patient)}
    >
      <Group gap="sm" wrap="nowrap">
        <Avatar radius="xl" size="sm">
          <IconUser size={14} />
        </Avatar>
        <Stack gap={2} flex={1}>
          <Text fw={600} size="sm" truncate>
            {name}
          </Text>
          <Group gap="sm">
            {amka && (
              <Group gap={3}>
                <IconId size={11} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {amka}
                </Text>
              </Group>
            )}
            {patient.birthDate && (
              <Group gap={3}>
                <IconCalendar size={11} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {formatDate(patient.birthDate)}
                </Text>
              </Group>
            )}
          </Group>
        </Stack>
      </Group>
    </Card>
  );
};

// ---------------------------------------------------------------------------

export const AdministrationPage: FC = () => {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { t } = useTranslation('lims');

  // Patient search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
  const [patientResults, setPatientResults] = useState<Patient[]>([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Invoice modal state
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  // Encounter modal state
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(
    null
  );

  // Edit patient modal state
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Payment search (scoped to selected patient)
  const {
    resources: invoices,
    loading: invoicesLoading,
    page: invoicePage,
    totalPages: invoiceTotalPages,
    totalCount: invoiceTotalCount,
    setPage: setInvoicePage,
    patients: invoicePatients,
    searchParams: invoiceSearchParams,
    fetchResources: fetchInvoices,
  } = usePaymentSearch(medplum, selectedPatient?.id);

  // Order search (scoped to selected patient)
  const {
    resources: orders,
    loading: ordersLoading,
    page: orderPage,
    totalPages: orderTotalPages,
    totalCount: orderTotalCount,
    setPage: setOrderPage,
    patients: orderPatients,
    fetchResources: fetchOrders,
    consents: orderConsents,
  } = useOrderSearch(medplum, selectedPatient?.id);

  // Encounter search (scoped to selected patient)
  const {
    resources: encounters,
    loading: encountersLoading,
    page: encounterPage,
    totalPages: encounterTotalPages,
    totalCount: encounterTotalCount,
    setPage: setEncounterPage,
    patients: encounterPatients,
    fetchResources: fetchEncounters,
  } = useEncounterSearch(medplum, selectedPatient?.id);

  // Encounter detail data (for modal)
  const {
    loading: encounterDetailLoading,
    patient: encounterPatient,
    attendingDoctor,
    referringDoctor,
    consultants,
    additionalNotes,
    encounterStatus,
  } = useEncounterDetails(selectedEncounter, medplum);

  // -------------------------------------------------------------------------
  // Patient search by name or AMKA — runs on every keystroke (debounced)
  useEffect(() => {
    const query = debouncedQuery.trim();
    if (!query || query.length < 2) {
      setPatientResults([]);
      return;
    }

    let cancelled = false;
    setPatientSearching(true);

    const params: Record<string, string> = validateAmka(query)
      ? { identifier: `${IDENTIFIERS.AMKA}|${query}`, _count: '20' }
      : { name: query, _count: '20' };

    medplum
      .searchResources('Patient', params)
      .then((results) => {
        if (!cancelled) {
          setPatientResults(results);
        }
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) {
          setPatientSearching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, medplum]);

  // Fetch all data whenever the selected patient changes
  useEffect(() => {
    if (selectedPatient) {
      fetchInvoices(PAYMENT_SEARCH_DEFAULTS);
      fetchOrders(ORDER_SEARCH_DEFAULTS);
      fetchEncounters(ENCOUNTER_SEARCH_DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.id]);

  // -------------------------------------------------------------------------
  const handleSelectPatient = useCallback((patient: Patient) => {
    setSelectedPatient(patient);
  }, []);

  const handleInvoiceClick = useCallback((invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceModalOpen(true);
  }, []);

  const handleInvoiceModalClose = useCallback(() => {
    setInvoiceModalOpen(false);
    setSelectedInvoice(null);
    if (selectedPatient) {
      fetchInvoices(invoiceSearchParams as PaymentSearchParams);
    }
  }, [fetchInvoices, invoiceSearchParams, selectedPatient]);

  const handleOrderClick = useCallback(
    (order: ServiceRequest) => {
      if (order.id) {
        navigate(`/lims/order/${order.id}`);
      }
    },
    [navigate]
  );

  const handleEncounterClick = useCallback((encounter: Encounter) => {
    setSelectedEncounter(encounter);
  }, []);

  const handleEncounterModalClose = useCallback(() => {
    setSelectedEncounter(null);
  }, []);

  // -------------------------------------------------------------------------
  const invoiceColumns = useMemo(
    () => getPaymentTableColumns({ t, patients: invoicePatients }),
    [invoicePatients, t]
  );

  const orderColumns = useMemo(
    () =>
      getOrderTableColumns({
        t,
        patients: orderPatients,
        hidePatientColumn: true,
        consents: orderConsents,
      }),
    [orderPatients, orderConsents, t]
  );

  const encounterColumns = useMemo(
    () =>
      getEncounterTableColumns({
        t: t as any,
        patients: encounterPatients,
        hidePatientColumn: true,
      }),
    [encounterPatients, t]
  );

  // -------------------------------------------------------------------------
  return (
    <Stack gap="md" className="p-6">
      <Group gap="sm">
        <IconSearch size={24} />
        <Title order={2}>
          {t('secretaryPaymentsPage.title', 'Patient Payments', { ns: 'lims' })}
        </Title>
      </Group>

      {/* Two-column layout: search panel (left) + content (right) */}
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Paper shadow="sm" withBorder p="md" w={'25%'}>
          <Stack gap="sm">
            <Text fw={600} size="sm">
              {t('secretaryPaymentsPage.searchPatient', 'Search Patient', {
                ns: 'lims',
              })}
            </Text>

            <TextInput
              placeholder={t(
                'secretaryPaymentsPage.searchPlaceholder',
                'Name or AMKA...',
                { ns: 'lims' }
              )}
              leftSection={<IconSearch size={16} />}
              rightSection={patientSearching ? <Loader size={14} /> : undefined}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
            />

            {/* Results list */}
            {patientResults.length > 0 && (
              <>
                <Divider />
                <Text size="xs" c="dimmed">
                  {patientResults.length}{' '}
                  {t('secretaryPaymentsPage.results', 'result(s)', {
                    ns: 'lims',
                  })}
                </Text>
                <ScrollArea.Autosize mah={400} offsetScrollbars>
                  <Stack gap={4}>
                    {patientResults.map((p) => (
                      <PatientRow
                        key={p.id}
                        patient={p}
                        onSelect={handleSelectPatient}
                      />
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              </>
            )}

            {/* No results */}
            {debouncedQuery.trim().length >= 2 &&
              !patientSearching &&
              patientResults.length === 0 && (
                <Text size="xs" c="dimmed" ta="center" py="sm">
                  {t('secretaryPaymentsPage.noPatients', 'No patients found', {
                    ns: 'lims',
                  })}
                </Text>
              )}
          </Stack>
        </Paper>

        <Stack gap="md" flex={1}>
          {selectedPatient ? (
            <>
              <Paper shadow="sm" withBorder p="md">
                <Group justify="space-between" align="flex-start">
                  <Group gap="sm" wrap="nowrap">
                    <Avatar color="blue" radius="xl" size="lg">
                      <IconUser size={22} />
                    </Avatar>
                    <Stack gap={4}>
                      <Text fw={700} size="lg" lh={1.2}>
                        {getPatientOrPractitionerName(selectedPatient)}
                      </Text>
                      <Group gap="lg">
                        {getPatientAmka(selectedPatient) && (
                          <Group gap={4}>
                            <IconId
                              size={13}
                              color="var(--mantine-color-dimmed)"
                            />
                            <Text size="xs" c="dimmed">
                              AMKA: {getPatientAmka(selectedPatient)}
                            </Text>
                          </Group>
                        )}
                        {selectedPatient.birthDate && (
                          <Group gap={4}>
                            <IconCalendar
                              size={13}
                              color="var(--mantine-color-dimmed)"
                            />
                            <Text size="xs" c="dimmed">
                              {formatDate(selectedPatient.birthDate)}
                            </Text>
                          </Group>
                        )}
                        {selectedPatient.gender && (
                          <Text size="xs" c="dimmed" tt="capitalize">
                            {selectedPatient.gender}
                          </Text>
                        )}
                      </Group>
                    </Stack>
                  </Group>
                  <EditButton onClick={() => setEditModalOpen(true)} />
                </Group>
              </Paper>

              <Tabs defaultValue="encounters">
                <Tabs.List>
                  <Tabs.Tab
                    value="encounters"
                    leftSection={<IconStethoscope size={16} />}
                  >
                    {t('secretaryPaymentsPage.tab.encounters', 'Encounters', {
                      ns: 'lims',
                    })}
                  </Tabs.Tab>

                  <Tabs.Tab
                    value="orders"
                    leftSection={<IconClipboardList size={16} />}
                  >
                    {t('secretaryPaymentsPage.tab.orders', 'Orders', {
                      ns: 'lims',
                    })}
                  </Tabs.Tab>

                  <Tabs.Tab
                    value="payments"
                    leftSection={<IconCash size={16} />}
                  >
                    {t('secretaryPaymentsPage.tab.payments', 'Payments', {
                      ns: 'lims',
                    })}
                  </Tabs.Tab>

                  <Tabs.Tab
                    value="timeline"
                    leftSection={<IconTimeline size={16} />}
                  >
                    {t('secretaryPaymentsPage.tab.timeline', 'Timeline', {
                      ns: 'lims',
                    })}
                  </Tabs.Tab>
                </Tabs.List>

                {/* Payments tab */}
                <Tabs.Panel value="payments" pt="md">
                  <Paper shadow="sm" withBorder>
                    {invoicesLoading ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : (
                      <ResourceTable<Invoice>
                        data={invoices}
                        columns={invoiceColumns}
                        page={invoicePage}
                        totalPages={invoiceTotalPages}
                        totalCount={invoiceTotalCount}
                        onPageChange={setInvoicePage}
                        onRowClick={handleInvoiceClick}
                        onActionClick={handleInvoiceClick}
                        emptyText={t(
                          'secretaryPaymentsPage.noPayments',
                          'No payments found for this patient',
                          { ns: 'lims' }
                        )}
                      />
                    )}
                  </Paper>
                </Tabs.Panel>

                {/* Orders tab */}
                <Tabs.Panel value="orders" pt="md">
                  <Paper shadow="sm" withBorder>
                    {ordersLoading ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : (
                      <ResourceTable<ServiceRequest>
                        data={orders}
                        columns={orderColumns}
                        page={orderPage}
                        totalPages={orderTotalPages}
                        totalCount={orderTotalCount}
                        onPageChange={setOrderPage}
                        onRowClick={handleOrderClick}
                        onActionClick={handleOrderClick}
                        emptyText={t(
                          'secretaryPaymentsPage.noOrders',
                          'No orders found for this patient',
                          { ns: 'lims' }
                        )}
                      />
                    )}
                  </Paper>
                </Tabs.Panel>

                {/* Encounters tab */}
                <Tabs.Panel value="encounters" pt="md">
                  <Paper shadow="sm" withBorder>
                    {encountersLoading ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : (
                      <ResourceTable<Encounter>
                        data={encounters}
                        columns={encounterColumns}
                        page={encounterPage}
                        totalPages={encounterTotalPages}
                        totalCount={encounterTotalCount}
                        onPageChange={setEncounterPage}
                        onRowClick={handleEncounterClick}
                        onActionClick={handleEncounterClick}
                        emptyText={t(
                          'secretaryPaymentsPage.noEncounters',
                          'No encounters found for this patient',
                          { ns: 'lims' }
                        )}
                      />
                    )}
                  </Paper>
                </Tabs.Panel>

                {/* Timeline tab */}
                <Tabs.Panel value="timeline" pt="md">
                  <Paper bg="transparent">
                    <PatientTimeline
                      encounters={encounters}
                      orders={orders}
                      invoices={invoices}
                      consents={orderConsents}
                      loading={
                        encountersLoading || ordersLoading || invoicesLoading
                      }
                      onEncounterClick={handleEncounterClick}
                      onOrderClick={handleOrderClick}
                      onInvoiceClick={handleInvoiceClick}
                    />
                  </Paper>
                </Tabs.Panel>
              </Tabs>
            </>
          ) : (
            <Paper shadow="sm" withBorder flex={1}>
              <Center h={300}>
                <Stack align="center" gap="sm">
                  <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                    <IconUserSearch size={24} />
                  </ThemeIcon>
                  <Text c="dimmed" size="sm" ta="center">
                    {t(
                      'secretaryPaymentsPage.selectPrompt',
                      'Search for a patient on the left to view their payments, orders and encounters',
                      { ns: 'lims' }
                    )}
                  </Text>
                </Stack>
              </Center>
            </Paper>
          )}
        </Stack>
      </Group>

      {/* Edit patient modal */}
      <PatientEditDemographicsModal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        patient={selectedPatient}
        onPatientSaved={(updated) => setSelectedPatient(updated)}
      />

      {/* Invoice detail modal */}
      <InvoiceDetailModal
        opened={invoiceModalOpen}
        onClose={handleInvoiceModalClose}
        invoice={selectedInvoice}
        patient={selectedPatient}
      />

      {/* Encounter detail modal */}
      <Modal
        opened={!!selectedEncounter}
        onClose={handleEncounterModalClose}
        size="lg"
        title={t(
          'secretaryPaymentsPage.encounterDetails',
          'Encounter Details',
          { ns: 'lims' }
        )}
        centered
      >
        {selectedEncounter && (
          <EncounterDetailsCard
            encounter={selectedEncounter}
            patient={encounterPatient}
            attendingDoctor={attendingDoctor}
            referringDoctor={referringDoctor}
            consultants={consultants}
            additionalNotes={additionalNotes}
            encounterStatus={encounterStatus}
            loading={encounterDetailLoading}
          />
        )}
      </Modal>
    </Stack>
  );
};
